import React, { useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import { Moment } from 'moment'
import { Options } from 'material-table'

import { ERRORS } from 'consts'
import { useApiCall } from 'hooks'
import { snackbar } from 'components'

import { DefaultTable } from '../../components'
import { getParkingMetricsByLevel } from './api'
import { formatMinutesAsHours, formatPercent, formatUsages } from './format'
import { useStyles } from './styles'
import {
  ParkingLevelMetricRow,
  ParkingMetricsByLevelResponse,
  ParkingMetricsDateParams,
  ParkingMetricsTotals,
} from './types'

interface Props {
  startDate: Moment
  endDate: Moment
}

interface TableRow {
  id: string | number
  levelLabel: string
  usagesLabel: string
  consumedMinutes: number
  grantedMinutes: number
  percentLabel: string
  isTotal?: boolean
}

const TOTAL_ROW_ID = '__parking_metrics_level_total__'

const tightCell = {
  whiteSpace: 'nowrap' as const,
  padding: '0 10px',
  height: 37,
  maxHeight: 37,
  lineHeight: '37px',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  boxSizing: 'border-box' as const,
}

const tightHeader = {
  whiteSpace: 'nowrap' as const,
  padding: '0 10px',
  height: 40,
  maxHeight: 40,
  lineHeight: '40px',
  boxSizing: 'border-box' as const,
}

const columns = [
  {
    title: 'Nivel',
    field: 'levelLabel',
    width: '16%',
    cellStyle: { ...tightCell, width: '16%', paddingLeft: 16 },
    headerStyle: { ...tightHeader, width: '16%', paddingLeft: 16 },
  },
  {
    title: 'Cantidad de usos',
    field: 'usagesLabel',
    width: '21%',
    cellStyle: { ...tightCell, width: '21%' },
    headerStyle: { ...tightHeader, width: '21%' },
  },
  {
    title: 'Horas consumidas',
    field: 'consumedMinutes',
    width: '21%',
    cellStyle: { ...tightCell, width: '21%' },
    headerStyle: { ...tightHeader, width: '21%' },
    render: (row: TableRow) => formatMinutesAsHours(row.consumedMinutes),
  },
  {
    title: 'Horas otorgadas',
    field: 'grantedMinutes',
    width: '21%',
    cellStyle: { ...tightCell, width: '21%' },
    headerStyle: { ...tightHeader, width: '21%' },
    render: (row: TableRow) => formatMinutesAsHours(row.grantedMinutes),
  },
  {
    title: '% consumidos',
    field: 'percentLabel',
    width: '21%',
    cellStyle: { ...tightCell, width: '21%' },
    headerStyle: { ...tightHeader, width: '21%' },
  },
]

const emptyTotals: ParkingMetricsTotals = {
  usages: 0,
  consumedMinutes: 0,
  grantedMinutes: 0,
}

const getTableOptions = (): Options => ({
  pageSize: 10,
  toolbar: false,
  paging: true,
  sorting: false,
  emptyRowsWhenPaging: false,
  rowStyle: (rowData: TableRow) => {
    if (rowData.isTotal) {
      return {
        height: 37,
        backgroundColor: '#F5F5F5',
        fontWeight: 600,
      }
    }
    return { height: 37 }
  },
  headerStyle: {
    height: 40,
    padding: 0,
  },
})

const ByLevelTable: React.FC<Props> = ({ startDate, endDate }) => {
  const classes = useStyles()
  const [rows, setRows] = useState<ParkingLevelMetricRow[]>([])
  const [totals, setTotals] = useState<ParkingMetricsTotals>(emptyTotals)
  const [requestMetrics, isLoading] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByLevelResponse
  >(getParkingMetricsByLevel)

  useEffect(() => {
    requestMetrics({ startDate, endDate })
      .then(resp => {
        setRows(resp?.data || [])
        setTotals(resp?.totals || emptyTotals)
      })
      .catch(() => {
        setRows([])
        setTotals(emptyTotals)
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const tableData: TableRow[] = useMemo(() => {
    const dataRows = rows.map(row => ({
      id: row.level,
      levelLabel: `Nivel ${row.level}`,
      usagesLabel: formatUsages(row.usages),
      consumedMinutes: row.consumedMinutes,
      grantedMinutes: row.grantedMinutes,
      percentLabel: formatPercent(row.consumedPercent),
    }))

    return [
      ...dataRows,
      {
        id: TOTAL_ROW_ID,
        levelLabel: 'Total',
        usagesLabel: formatUsages(totals.usages),
        consumedMinutes: totals.consumedMinutes,
        grantedMinutes: totals.grantedMinutes,
        percentLabel: '',
        isTotal: true,
      },
    ]
  }, [rows, totals])

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" component="h2" className={classes.title}>
          Horas de parking por nivel
        </Typography>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.tableWrap}>
          <DefaultTable
            title="Parking por nivel"
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            options={getTableOptions()}
          />
        </div>
      </Grid>
    </>
  )
}

export { ByLevelTable }
